<script setup lang="ts">
import {onMounted, PropType, Ref, ref, useTemplateRef} from "vue";
import {ICellEditorParams} from "ag-grid-community";
import Translator from "@/utils/common/Translator.ts";
import {SearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {QSelect} from "quasar";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";

const props = defineProps({
  params: {
    type: Object as PropType<ICellEditorParams>,
    required: true
  }
});
const allowedValues: string[] = props.params['allowedValues'] ?? [];
const enumTypeName: string = props.params['enumTypeName'] ?? '';
const allowedSearchItems: SearchItem[] = initAllowedSearchItems();
const selectedItem: Ref<SearchItem | null | undefined> = ref(props.params.value ? allowedSearchItems.find(l => l.id === props.params.value) : null)
const displayedText = ref<string | null | undefined>(selectedItem.value?.text);
const options = ref<SearchItem[]>(fetchFilteredItems(props.params.value));
const qSelect = useTemplateRef<QSelect>("qSelect");
const popupVisible = ref(false);

function initAllowedSearchItems() {
  const items: SearchItem[] = [];
  for (let value of allowedValues) {
    let ar = Translator.isArabic() ? Translator.translate(value, enumTypeName)! : Translator.translateInOtherLang(value, enumTypeName)!;
    let en = !Translator.isArabic() ? Translator.translate(value, enumTypeName)! : Translator.translateInOtherLang(value, enumTypeName)!;
    let searchData = EditScreenFieldUtils.fetchSearchTextForAllowedValue(value, enumTypeName);
    items.push({
      id: value,
      ar: ar,
      en: en,
      text: Translator.translate(value, enumTypeName)!,
      searchData: searchData
    } as SearchItem);
  }
  items.sort((a, b) => a.text.localeCompare(b.text))
  return items;
}

function fetchFilteredItems(value: string | null | undefined) {
  if (!value)
    return allowedSearchItems;
  return allowedSearchItems.filter(e =>
    e.searchData && (e.searchData.includes(value)
      || e.searchData.includes(StringUtils.normalizeArabic(value))
      || e.searchData.includes(StringUtils.normalizeArabic(StringUtils.englishToArabic(value)))
      || e.searchData.includes(StringUtils.arabicToEnglish(value).toLowerCase())))
    .sort((a, b) => a.id === b.id ? 0 : a.searchData!.length - b.searchData!.length);
}

function filter(value: string | null | undefined) {
  options.value = fetchFilteredItems(value);
}

function onFilter(val, update) {
  update(() => filter(val),
    ref => {
      if (val !== '' && ref.options.length > 0) {
        ref.setOptionIndex(-1);
        ref.moveOptionSelection(1, true);
      }
    })
}

function getValue() {
  return selectedItem.value?.id;
}

onMounted(() => {
  if (qSelect.value) {
    setTimeout(() => {
      qSelect.value?.focus();
      if (props.params?.eventKey?.length === 1) {
        qSelect.value?.updateInputValue(props.params?.eventKey)
      }
    })
  }
});

function onPartialUserInput(val) {
  if (!val)
    selectedItem.value = null;
  displayedText.value = val;
}

function onSelect(value?: SearchItem) {
  selectedItem.value = value;
  props.params?.stopEditing();
  setTimeout(async () => {
    await useLoadingStore().awaitLoadingFinish(10);
    AGGridUtils.navigateToNextCellAndStartEditing(props.params!.api);
  })
}

function onEnterKeyDown(e: KeyboardEvent) {
  if (AGGridUtils.isBulkAction(e)) {
    const search = displayedText.value || '';
    const searchedItems = fetchFilteredItems(search);
    const itemIdToShow = searchedItems.length > 0 ? searchedItems[0].id ?? '' : '';
    namaUpdateValue(itemIdToShow);
    AGGridUtils.dispatchBulkEventFor(qSelect);
    return;
  }

  if (!popupVisible.value) {
    props.params?.stopEditing();
    AGGridUtils.navigateToNextCellAndStartEditing(props.params!.api);
  }
}

function isAllowedValueMatched(e: SearchItem, val: string) {
  return e.searchData && (e.searchData.toLowerCase().includes(val)
    || e.searchData.toLowerCase().includes(StringUtils.normalizeArabic(val))
    || e.searchData.toLowerCase().includes(StringUtils.normalizeArabic(StringUtils.englishToArabic(val)))
    || e.searchData.toLowerCase().includes(StringUtils.arabicToEnglish(val).toLowerCase()));
}

function namaUpdateValue(value: string) {
  selectedItem.value = value ? allowedSearchItems.find(l => isAllowedValueMatched(l, value)) : undefined;
  displayedText.value = selectedItem.value?.text;
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'F2') {
    let inputFieldValue = displayedText.value;
    if ((!inputFieldValue && selectedItem.value) || (selectedItem.value && selectedItem.value.id === inputFieldValue))
      return;
    selectedItem.value = inputFieldValue ? allowedSearchItems.find(l => isAllowedValueMatched(l, inputFieldValue)) : undefined;
    props.params.api.stopEditing();
  } else if (event.key === 'Escape') {
    props.params.api.stopEditing();
    props.params.api.setFocusedCell(props.params.rowIndex, props.params.column);
  }
}

defineExpose({getValue, namaUpdateValue});
</script>

<template>
  <div class="tw-mb-0 tw-h-full grid-enum-selector tw-max-w-full tw-w-full" @keydown="onKeyDown">
    <div class="tw-relative tw-leading-tight tw-h-full tw-max-w-full tw-w-full">
      <QSelect
        @keydown.enter="onEnterKeyDown"
        ref="qSelect"
        use-input hide-selected fill-input dense options-dense
        input-debounce="0"
        :options="options"
        option-label="text"
        option-value="id"
        @filter="onFilter"
        @input-value="onPartialUserInput"
        :model-value="selectedItem"
        @popup-hide="popupVisible = false"
        @popup-show="popupVisible = true"
        @update:model-value="onSelect"
        class="tw-bg-[#F8F9FA] tw-shadow tw-appearance-none dark:tw-bg-[#3E3E3E] tw-h-full tw-min-h-full ag-custom-component-popup"
        input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-namaFontColor-dark
      dark:tw-placeholder-gray-300"
        popup-content-class="!tw-max-h-[30vh] navbar-search-popup dark:tw-bg-namaBackground-deep-dark dark:tw-text-namaFontColor-dark ag-custom-component-popup"
        options-selected-class="tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark nama-icon-active">
        <template v-slot:option="scope">
          <q-item v-bind="scope.itemProps">
            <q-item-section>
              <div class="tw-flex tw-items-center tw-gap-2">
                <NamaIcon :enum-type-name="enumTypeName" :allowed-value="scope.opt.id" :key="scope.opt.id"/>
                <q-item-label class="tw-text-sm dark:tw-text-namaFontColor-dark" v-text="Translator.translateId(scope.opt.id,enumTypeName)"/>
              </div>
            </q-item-section>
          </q-item>
        </template>
      </QSelect>
    </div>
  </div>
</template>

<style>
.grid-enum-selector .q-field__native {
  padding-top: 0;
  padding-bottom: 0;
  min-height: 100% !important;
}

.grid-enum-selector .q-field__control.relative-position,
.grid-enum-selector .q-field__append.q-field__marginal {
  min-height: 100% !important;
  height: 100% !important;
}
</style>
